import { createFormErrorMessage } from '@/components/FormErrorMessage';
import settings from '@/constants/constants';
import { triageSteps } from '@/data/triage-steps';
import { useD2DTracking } from '@/hooks/useD2DTracking';
import { WithPageWrapper } from '@/pages/components/Branding';
import { RoutedFormBuilder } from '@montugroup/form-builder/routing';
import type { apiPatientPortalContract } from '@montugroup/pms-api-contracts';
import type { FieldValues } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import type { z } from 'zod';
import { FormStep } from '../components/Form';
import { handleTriageAnswersTransform } from './transformTriageAnswers';

type SubmitTriageFormResponse = z.infer<(typeof apiPatientPortalContract.submitTriageForm.responses)[200]>;

export const TRIAGE_POST_URL = `${settings.xApiUrl}/patient-portal/v1/triage-form`;

const RESULT_DESTINATION = {
  simple: '/direct-to-doctor',
  complex: '/consult-eligible-nurse',
} as const;

const TriageErrorMessage = createFormErrorMessage({});
export const TriageForm = () => {
  const FORM_ID = 'triage';

  const navigate = useNavigate();
  const { search } = useLocation();
  const { trackTriageStarted, trackTriageCompleted } = useD2DTracking();

  const handleAfterSubmitSuccess = (_formValues: FieldValues, responseBody?: object) => {
    const { data } = (responseBody ?? {}) as SubmitTriageFormResponse;
    const result = data?.result;

    trackTriageCompleted(result ?? null);

    navigate(`${result === 'simple' ? RESULT_DESTINATION.simple : RESULT_DESTINATION.complex}${search}`);
  };

  return WithPageWrapper(
    <RoutedFormBuilder
      basePath="/triage"
      formId={FORM_ID}
      postUrl={TRIAGE_POST_URL}
      steps={triageSteps({ onStart: trackTriageStarted })}
      transform={handleTriageAnswersTransform}
      errorSlot={TriageErrorMessage}
      afterSubmitSuccess={handleAfterSubmitSuccess}
      stepButtonsConfig={{ fullWidthStepButtons: true }}
      showStepIndicator={({ currentStep, totalSteps }) => (
        <FormStep>
          Question {currentStep} of {totalSteps}
        </FormStep>
      )}
    />,
    true,
  );
};
